// QHearts Personality-Profile schema
// Each section has a list of fields. Field types drive the editor UI.
//
// Field types:
//   "text"        – single line input
//   "textarea"    – multi-line input
//   "number"      – numeric input
//   "date"        – ISO date input
//   "radio"       – single choice (options[])
//   "multiselect" – multiple choice (options[])
//   "slider"      – 0..100 with leftLabel/rightLabel
//   "yesno"       – yes/no toggle
//   "url"         – URL input
//
// Use `optional: true` for fields that should not count toward completion.

export const profileSchema = [
  {
    id: "basics",
    title: "Basisinformationen",
    description: "Die Grundlage deines Profils.",
    fields: [
      { key: "name", label: "Vorname / Anzeigename", type: "text", placeholder: "z.B. Anna" },
      { key: "birth_date", label: "Geburtsdatum", type: "date" },
      { key: "city", label: "Wohnort", type: "text", placeholder: "z.B. Berlin" },
      { key: "zip_code", label: "PLZ", type: "text", placeholder: "z.B. 10115" },
      {
        key: "gender",
        label: "Geschlecht",
        type: "radio",
        options: ["weiblich", "männlich", "divers", "andere"],
      },
      {
        key: "interested_in",
        label: "Gesuchtes Geschlecht / Orientierung",
        type: "multiselect",
        options: ["weiblich", "männlich", "divers", "alle"],
      },
      {
        key: "relationship_status",
        label: "Beziehungsstatus",
        type: "radio",
        options: ["Single", "In Beziehung", "Getrennt lebend", "Es ist kompliziert"],
      },
    ],
  },
  {
    id: "presentation",
    title: "Visuelle & emotionale Selbstdarstellung",
    description: "Wie willst du dich zeigen?",
    fields: [
      {
        key: "headline",
        label: "Profil-Headline",
        type: "text",
        placeholder: "Kurzbeschreibung in einem Satz",
        maxLength: 80,
      },
      {
        key: "bio",
        label: "Selbstbeschreibung",
        type: "textarea",
        placeholder: "3–4 Sätze über deine Persönlichkeit",
        maxLength: 600,
      },
      {
        key: "mood_themes",
        label: "Mood / Themen",
        type: "multiselect",
        options: [
          "Reisen",
          "Musik",
          "Lifestyle",
          "Kunst",
          "Sport",
          "Natur",
          "Kulinarik",
          "Mode",
          "Film & Serien",
          "Gaming",
        ],
      },
      {
        key: "video_intro_url",
        label: "Video-Intro (Link)",
        type: "url",
        optional: true,
        placeholder: "https://...",
      },
      {
        key: "voice_intro_url",
        label: "Sprach-Intro (Link)",
        type: "url",
        optional: true,
        placeholder: "https://...",
      },
    ],
  },
  {
    id: "interests",
    title: "Interessen & Lifestyle",
    description: "Was bewegt dich im Alltag?",
    fields: [
      {
        key: "interests",
        label: "Hobbys & Freizeit",
        type: "multiselect",
        options: [
          "Sport",
          "Musik",
          "Reisen",
          "Kunst",
          "Gaming",
          "Kochen",
          "Lesen",
          "Yoga",
          "Wandern",
          "Tanzen",
          "Fotografie",
          "Konzerte",
          "Festivals",
        ],
      },
      { key: "favorite_music", label: "Lieblingsmusik / Künstler", type: "text" },
      { key: "favorite_movies", label: "Filme / Serien / Bücher", type: "text" },
      { key: "favorite_food", label: "Lieblingsessen / Küche", type: "text" },
      {
        key: "pets",
        label: "Haustiere",
        type: "radio",
        options: ["Ja, ich habe", "Nein, mag aber", "Nein, mag keine", "Allergisch"],
      },
      {
        key: "travel_types",
        label: "Reisetyp",
        type: "multiselect",
        options: ["Strand", "Stadt", "Abenteuer", "Natur", "Kultur", "Festivals", "Roadtrip"],
      },
      {
        key: "party_quiet_score",
        label: "Partytyp ↔ Ruhepol",
        type: "slider",
        leftLabel: "Ruhepol",
        rightLabel: "Partytyp",
      },
      {
        key: "sport_level",
        label: "Sportlevel",
        type: "radio",
        options: ["Gar nicht", "Gelegentlich", "Regelmäßig", "Täglich"],
      },
    ],
  },
  {
    id: "personality",
    title: "Charakter & Persönlichkeit",
    description: "Wer bist du wirklich?",
    fields: [
      {
        key: "three_words",
        label: "Drei Wörter, die dich beschreiben",
        type: "text",
        placeholder: "z.B. ehrlich, neugierig, warm",
        maxLength: 60,
      },
      {
        key: "friends_view",
        label: "Wie sehen Freunde dich?",
        type: "textarea",
        maxLength: 400,
      },
      {
        key: "intro_extro_score",
        label: "Introvertiert ↔ Extrovertiert",
        type: "slider",
        leftLabel: "Introvertiert",
        rightLabel: "Extrovertiert",
      },
      {
        key: "spontaneity_score",
        label: "Geplant ↔ Spontan",
        type: "slider",
        leftLabel: "Geplant",
        rightLabel: "Spontan",
      },
      {
        key: "empathy_score",
        label: "Empathie",
        type: "slider",
        leftLabel: "Niedrig",
        rightLabel: "Hoch",
      },
      {
        key: "humor_score",
        label: "Humor",
        type: "slider",
        leftLabel: "Trocken",
        rightLabel: "Verspielt",
      },
      {
        key: "ambition_score",
        label: "Ehrgeiz",
        type: "slider",
        leftLabel: "Entspannt",
        rightLabel: "Ehrgeizig",
      },
      {
        key: "creativity_score",
        label: "Kreativität",
        type: "slider",
        leftLabel: "Strukturiert",
        rightLabel: "Kreativ",
      },
      {
        key: "values",
        label: "Werte & Einstellungen",
        type: "multiselect",
        options: [
          "Familie",
          "Karriere",
          "Spiritualität",
          "Freundschaft",
          "Gesundheit",
          "Freiheit",
          "Sicherheit",
          "Abenteuer",
          "Nachhaltigkeit",
          "Bildung",
        ],
      },
      {
        key: "relationship_priorities",
        label: "Was ist dir in einer Beziehung wichtig?",
        type: "multiselect",
        options: [
          "Treue",
          "Kommunikation",
          "Humor",
          "Intimität",
          "Vertrauen",
          "Unabhängigkeit",
          "Geborgenheit",
          "Gemeinsame Werte",
          "Abenteuer",
        ],
      },
      {
        key: "love_languages",
        label: "Liebessprachen-Profil",
        type: "multiselect",
        options: [
          "Zuneigung",
          "Gemeinsame Zeit",
          "Geschenke",
          "Liebevolle Worte",
          "Hilfsbereite Taten",
        ],
      },
    ],
  },
  {
    id: "career",
    title: "Beruf & Bildung",
    description: "Was machst du im Leben?",
    fields: [
      { key: "profession", label: "Beruf", type: "text" },
      { key: "industry", label: "Branche", type: "text" },
      { key: "education", label: "Ausbildung / Studium", type: "text" },
      {
        key: "work_life_balance_score",
        label: "Karriere ↔ Freizeit",
        type: "slider",
        leftLabel: "Karriereorientiert",
        rightLabel: "Freizeitliebend",
      },
    ],
  },
  {
    id: "lifestyle",
    title: "Lebensstil & Alltag",
    description: "Wie sieht dein Alltag aus?",
    fields: [
      {
        key: "daily_rhythm",
        label: "Tagesrhythmus",
        type: "radio",
        options: ["Frühaufsteher", "Nachtmensch", "Flexibel"],
      },
      {
        key: "diet",
        label: "Ernährungsweise",
        type: "radio",
        options: ["Allesesser", "Vegetarier", "Vegan", "Pescetarier", "Andere"],
      },
      {
        key: "smoking",
        label: "Rauchen",
        type: "radio",
        options: ["Nie", "Gelegentlich", "Regelmäßig"],
      },
      {
        key: "alcohol",
        label: "Alkohol",
        type: "radio",
        options: ["Nie", "Gelegentlich", "Regelmäßig"],
      },
      {
        key: "cannabis",
        label: "Cannabis",
        type: "radio",
        optional: true,
        options: ["Nie", "Gelegentlich", "Regelmäßig"],
      },
      {
        key: "mobility",
        label: "Mobilität",
        type: "multiselect",
        options: ["Auto", "Fahrrad", "Zug", "Motorrad", "ÖPNV", "Zu Fuß", "Roller"],
      },
    ],
  },
  {
    id: "matching",
    title: "Matching-Präferenzen",
    description: "Wen möchtest du finden?",
    fields: [
      {
        key: "looking_for",
        label: "Was suchst du?",
        type: "radio",
        options: ["Freundschaft", "Flirt", "Beziehung", "Dating", "Offen"],
      },
      { key: "age_range_min", label: "Mindestalter", type: "number", min: 18, max: 99 },
      { key: "age_range_max", label: "Höchstalter", type: "number", min: 18, max: 99 },
      { key: "max_distance_km", label: "Maximale Entfernung (km)", type: "number", min: 1, max: 1000 },
      {
        key: "children_status",
        label: "Kinder vorhanden?",
        type: "radio",
        options: ["Keine", "Ja, leben bei mir", "Ja, leben woanders"],
      },
      {
        key: "family_plans",
        label: "Kinderwunsch",
        type: "radio",
        options: ["Ja", "Irgendwann", "Nein", "Unentschlossen"],
      },
    ],
  },
  {
    id: "verification",
    title: "Verifizierung & Vertrauen",
    description: "So zeigst du, dass du echt bist.",
    fields: [
      {
        key: "instagram_url",
        label: "Instagram",
        type: "url",
        optional: true,
        placeholder: "https://instagram.com/...",
      },
      {
        key: "spotify_url",
        label: "Spotify",
        type: "url",
        optional: true,
        placeholder: "https://open.spotify.com/...",
      },
      {
        key: "tiktok_url",
        label: "TikTok",
        type: "url",
        optional: true,
        placeholder: "https://tiktok.com/@...",
      },
    ],
  },
  {
    id: "icebreaker",
    title: "Persönliche Touch-Fragen",
    description: "Kleine Funken, die ein Gespräch starten.",
    fields: [
      {
        key: "icebreaker_laugh",
        label: "Was bringt dich zum Lachen?",
        type: "textarea",
        maxLength: 300,
      },
      {
        key: "icebreaker_sunday",
        label: "Dein perfekter Sonntag?",
        type: "textarea",
        maxLength: 300,
      },
      {
        key: "icebreaker_dream_date",
        label: "Was wäre dein Traumdate?",
        type: "textarea",
        maxLength: 300,
      },
      {
        key: "icebreaker_fridge",
        label: "Was darf bei dir nie im Kühlschrank fehlen?",
        type: "text",
        maxLength: 120,
      },
      {
        key: "icebreaker_song",
        label: "Welches Lied beschreibt dich?",
        type: "text",
        maxLength: 120,
      },
      {
        key: "icebreaker_heart",
        label: "Wofür schlägt dein Herz?",
        type: "textarea",
        maxLength: 400,
        signature: true,
      },
    ],
  },
];

// ---- Completion helpers ----
export function isFieldFilled(value, field) {
  if (value === null || value === undefined) return false;
  if (field.type === "multiselect") return Array.isArray(value) && value.length > 0;
  if (field.type === "slider" || field.type === "number") {
    return typeof value === "number" && !Number.isNaN(value);
  }
  if (typeof value === "string") return value.trim().length > 0;
  return Boolean(value);
}

export function sectionCompletion(section, profile) {
  const required = section.fields.filter((f) => !f.optional);
  if (required.length === 0) return { filled: 0, total: 0, percent: 0 };
  const filled = required.filter((f) => isFieldFilled(profile?.[f.key], f)).length;
  return {
    filled,
    total: required.length,
    percent: Math.round((filled / required.length) * 100),
  };
}

export function totalCompletion(profile) {
  let filled = 0;
  let total = 0;
  for (const section of profileSchema) {
    const required = section.fields.filter((f) => !f.optional);
    total += required.length;
    filled += required.filter((f) => isFieldFilled(profile?.[f.key], f)).length;
  }
  return {
    filled,
    total,
    percent: total === 0 ? 0 : Math.round((filled / total) * 100),
  };
}
